import { c as _c } from "react/compiler-runtime";
import { FathomEvent } from "@lib/constants";
import { useApplicationTranslation } from "@lib/hooks/translations";
import useLanguage from "@lib/hooks/useLanguage";
import usePartnerContext from "@lib/hooks/usePartnerContext";
import useTrackEvent from "@lib/hooks/useTrackEvent";
import { ApplicationFormValues } from "@lib/mapApplicationData/types";
import { applicationTypeToPath, getApplicationProcessPath } from "@lib/routing";
import FormScreen from "@modules/application/components/FormScreen";
import useApplicationContext from "@modules/application/hooks/useApplicationContext";
import { usesAppAccessLeadWall } from "@modules/application/components/LeadWall/FinalLeadWall";
import useSteps from "@modules/application/hooks/useSteps";
import getApplicationProcessSteps from "@modules/application/lib/applicationProcess";
import { ApplicationStepId } from "@modules/application/lib/applicationProcess/types";
import { useRouter } from "@lib/hooks/useRouter";
import { FC, useEffect, useMemo } from "react";
export type ApplicationProcessTypes = ApplicationFormValues["applicationType"];
interface ApplicationProcessProps {
  applicationType?: ApplicationProcessTypes;
  hasZeroStep: boolean;
  skipRealEstateAgentFee?: boolean;
  isCertificate?: boolean;
  stepId: string;
}
const ApplicationProcess: FC<ApplicationProcessProps> = t0 => {
  const $ = _c(39);
  const {
    stepId,
    applicationType,
    hasZeroStep,
    skipRealEstateAgentFee: t1,
    isCertificate: t2
  } = t0;
  const skipRealEstateAgentFee = t1 === undefined ? false : t1;
  const isCertificate = t2 === undefined ? false : t2;
  useTrackEvent(FathomEvent.APPLICATION_STARTED);
  const {
    t
  } = useApplicationTranslation();
  const {
    dispatch,
    state
  } = useApplicationContext();
  const {
    partner
  } = usePartnerContext();
  const router = useRouter();
  const locale = useLanguage();
  const t3 = partner?.slug;
  let t4;
  if ($[0] !== applicationType || $[1] !== isCertificate || $[2] !== locale || $[3] !== skipRealEstateAgentFee || $[4] !== t || $[5] !== t3) {
    t4 = getApplicationProcessSteps(t, {
      locale,
      partnerSlug: t3,
      skipRealEstateAgentFee,
      isCertificate,
      applicationType
    });
    $[0] = applicationType;
    $[1] = isCertificate;
    $[2] = locale;
    $[3] = skipRealEstateAgentFee;
    $[4] = t;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const steps = t4;
  let t5;
  if ($[7] !== applicationType || $[8] !== dispatch || $[9] !== hasZeroStep || $[10] !== isCertificate || $[11] !== locale || $[12] !== partner || $[13] !== router || $[14] !== state.applicationFormState || $[15] !== state.lead || $[16] !== stepId || $[17] !== steps) {
    t5 = {
      stepId,
      steps,
      hasZeroStep,
      formState: state.applicationFormState,
      onInvalidState() {
        router.replace(getApplicationProcessPath(locale, applicationTypeToPath(locale, applicationType), undefined, partner?.id));
      },
      onGoBack() {
        router.back();
      },
      onStepChange(step) {
        const path = [];
        if (isCertificate) {
          path.push("certificate");
        }
        const selectedApplicationType = applicationType ?? state.applicationFormState.values[ApplicationStepId.applicationType]?.applicationType;
        const skipsLeadWall = state.lead && (isCertificate || !usesAppAccessLeadWall(selectedApplicationType, !!partner));
        if (step.id === ApplicationStepId.leadWall && skipsLeadWall) {
          router.push(getApplicationProcessPath(locale, state.lead.isOffersViewForbidden ? "forbidden" : "offers", undefined, partner?.id));
          return;
        }
        if (applicationType && !isCertificate) {
          path.push(applicationTypeToPath(locale, applicationType));
        }
        path.push(step.id);
        router.push(getApplicationProcessPath(locale, path.join("/"), undefined, partner?.id));
      },
      onSetForm(form) {
        dispatch({
          type: "SET_APPLICATION_FORM_STATE",
          form
        });
      },
      onComplete(step_0) {
        if (step_0.navigateTo) {
          router.push(step_0.navigateTo);
        }
      }
    };
    $[7] = applicationType;
    $[8] = dispatch;
    $[9] = hasZeroStep;
    $[10] = isCertificate;
    $[11] = locale;
    $[12] = partner;
    $[13] = router;
    $[14] = state.applicationFormState;
    $[15] = state.lead;
    $[16] = stepId;
    $[17] = steps;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  const {
    handleNextStep,
    handlePreviousStep,
    nextSteps,
    value,
    numberOfSteps,
    currentStepNumber,
    currentStep
  } = useSteps(t5);
  let t6;
  let t7;
  if ($[19] !== applicationType || $[20] !== dispatch || $[21] !== state.applicationFormState) {
    t6 = () => {
      if (!applicationType || state.applicationFormState.values[ApplicationStepId.applicationType]) {
        return;
      }
      if (applicationType) {
        dispatch({
          type: "SET_APPLICATION_FORM_VALUE",
          stepId: ApplicationStepId.applicationType,
          value: {
            applicationType
          }
        });
      }
    };
    t7 = [applicationType, dispatch, state.applicationFormState];
    $[19] = applicationType;
    $[20] = dispatch;
    $[21] = state.applicationFormState;
    $[22] = t6;
    $[23] = t7;
  } else {
    t6 = $[22];
    t7 = $[23];
  }
  useEffect(t6, t7);
  let t8;
  if ($[24] !== currentStep) {
    t8 = [currentStep];
    $[24] = currentStep;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  useEffect(_temp, t8);
  let t10;
  let t9;
  if ($[26] !== dispatch) {
    t9 = () => {
      dispatch({
        type: "DELETE_APPLICATION"
      });
    };
    t10 = [dispatch];
    $[26] = dispatch;
    $[27] = t10;
    $[28] = t9;
  } else {
    t10 = $[27];
    t9 = $[28];
  }
  useEffect(t9, t10);
  const Component = currentStep.component || FormScreen;
  let t11;
  if ($[29] !== Component || $[30] !== currentStep || $[31] !== currentStepNumber || $[32] !== handleNextStep || $[33] !== handlePreviousStep || $[34] !== hasZeroStep || $[35] !== nextSteps || $[36] !== numberOfSteps || $[37] !== value) {
    t11 = <Component key={currentStep.id} steps={nextSteps} onPrevious={handlePreviousStep} onNext={handleNextStep} numberOfSteps={numberOfSteps} hasZeroStep={hasZeroStep} currentStepNumber={currentStepNumber} step={currentStep} value={value} />;
    $[29] = Component;
    $[30] = currentStep;
    $[31] = currentStepNumber;
    $[32] = handleNextStep;
    $[33] = handlePreviousStep;
    $[34] = hasZeroStep;
    $[35] = nextSteps;
    $[36] = numberOfSteps;
    $[37] = value;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
};
export default ApplicationProcess;
function _temp() {
  window.scrollTo(0, 0);
}